import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import clsx from 'clsx';
import { api } from '../../shared/api';
import { fmtTime } from '../../shared/dates';
import { ServiceLabels } from '@census/shared';
import { Icon, type IconName } from '../../ui/Icon';

/**
 * Resource Calendar — primary scheduling surface.
 * Columns are Resources; rows are 30-minute slots.
 * Provider cells show single appointment chips; group cells show N/Max
 * counts and open a roster modal on click.
 */
export function ResourceCalendarView({ date }: { date: string }) {
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [openCell, setOpenCell] = useState<{
    resourceId: string;
    resourceName: string;
    resourceType: string;
    startMin: number;
    endMin: number;
    clients: Array<{
      id: string | null;
      name: string;
      cohortId: string | null;
      status: string;
      instanceId?: string;
    }>;
  } | null>(null);

  const { data } = useQuery({
    queryKey: ['resource-day', date, typeFilter],
    queryFn: () =>
      api
        .get('/schedule/day', { params: { date, resourceType: typeFilter || undefined } })
        .then((r) => r.data),
  });

  const startHour = 7;
  const endHour = 21;
  const rowMinutes = 30;
  const rows = useMemo(() => {
    const out: number[] = [];
    for (let m = startHour * 60; m <= endHour * 60; m += rowMinutes) out.push(m);
    return out;
  }, []);

  if (!data) return <div className="card p-6 text-sm text-ink-subtle">Loading…</div>;

  const resources = data.resources as Array<{
    id: string;
    type: string;
    displayName: string;
    capacity: number;
    placeOfService: string;
    providerRole: string | null;
    groupServiceType: string | null;
  }>;

  const cells = data.cells as Array<{
    resourceId: string;
    startMin: number;
    endMin: number;
    kind: 'GROUP' | 'INDIVIDUAL';
    serviceType: string;
    placeOfService: string;
    clients: Array<{ id: string | null; name: string; cohortId: string | null; instanceId: string; status: string }>;
  }>;

  return (
    <div className="space-y-3">
      {/* Compact toolbar */}
      <div className="card flex flex-wrap items-center justify-between gap-3 px-4 py-2.5">
        <div className="flex items-center gap-3">
          <select
            className="input h-9 w-auto py-1.5"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            aria-label="Filter resources"
          >
            <option value="">All resources</option>
            <option value="PROVIDER">Providers (office)</option>
            <option value="COMMUNITY_PROVIDER">Community providers</option>
            <option value="GROUP">Groups</option>
            <option value="ROOM">Rooms</option>
          </select>
          <span className="hidden text-xs text-ink-subtle sm:inline">
            {resources.length} columns · group cells show <span className="font-medium text-ink-muted">N/Max</span>
          </span>
        </div>
        <Legend />
      </div>

      {/* Calendar */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <div
            className="grid min-w-[1200px]"
            style={{
              gridTemplateColumns: `72px repeat(${resources.length}, minmax(140px, 1fr))`,
            }}
          >
            {/* Header row */}
            <div className="sticky top-0 z-10 border-b border-line bg-surface-subtle px-2 py-2 text-2xs font-medium uppercase tracking-wide text-ink-subtle">
              Time
            </div>
            {resources.map((r) => (
              <ResourceHeader key={r.id} resource={r} />
            ))}

            {/* Body */}
            {rows.map((minute, idx) => (
              <RowSlot
                key={minute}
                minute={minute}
                idx={idx}
                rowMinutes={rowMinutes}
                resources={resources}
                cells={cells}
                onOpen={(c, resourceName, resourceType) =>
                  setOpenCell({
                    resourceId: c.resourceId,
                    resourceName,
                    resourceType,
                    startMin: c.startMin,
                    endMin: c.endMin,
                    // Codex finding: dropping instanceId here broke every
                    // action button in the modal (the guard fell through to
                    // "No AppointmentInstance row"). The API has always
                    // returned it on `cell.clients[i].instanceId`.
                    clients: c.clients.map((cl) => ({
                      id: cl.id,
                      name: cl.name,
                      cohortId: cl.cohortId,
                      status: cl.status,
                      instanceId: cl.instanceId,
                    })),
                  })
                }
              />
            ))}
          </div>
        </div>
      </div>

      {openCell && (
        <GroupRosterModal cell={openCell} onClose={() => setOpenCell(null)} />
      )}
    </div>
  );
}

function Legend() {
  return (
    <div className="hidden items-center gap-3 text-2xs text-ink-subtle sm:flex">
      <span className="flex items-center gap-1.5">
        <span className="h-2 w-2 rounded-full bg-emerald-400" />
        Individual
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-2 w-2 rounded-full bg-brand-500" />
        Group
      </span>
    </div>
  );
}

function ResourceHeader({
  resource,
}: {
  resource: {
    id: string;
    type: string;
    displayName: string;
    capacity: number;
    placeOfService: string;
    providerRole: string | null;
    groupServiceType: string | null;
  };
}) {
  const isGroup = resource.type === 'GROUP';
  const isCommunity = resource.type === 'COMMUNITY_PROVIDER';
  const isRoom = resource.type === 'ROOM';
  const icon: IconName = isGroup ? 'users' : isCommunity ? 'home-heart' : isRoom ? 'building' : 'user';

  // Color-coded place chip: Off (slate), Comm (amber). Telehealth is a delivery
  // mode shown per-cell, not a place.
  const place = resource.placeOfService;
  const placeBadge =
    place === 'HOME_COMMUNITY'
      ? { label: 'Comm', cls: 'bg-amber-50 text-amber-700 ring-amber-200', full: 'Home / community' }
      : { label: 'Off', cls: 'bg-slate-100 text-slate-700 ring-slate-200', full: 'Office' };

  // Resource-type icon color: provider=slate, community=amber, group=brand, room=emerald
  const iconCls = isGroup
    ? 'text-brand-600'
    : isCommunity
      ? 'text-amber-600'
      : isRoom
        ? 'text-emerald-600'
        : 'text-slate-500';

  // Provider role short code (e.g., THERAPIST → "Thrp", PRESCRIBER → "Rx", NURSE → "Nrs")
  const role = resource.providerRole;
  const roleShort =
    role === 'THERAPIST'
      ? 'Thrp'
      : role === 'PRESCRIBER'
        ? 'Rx'
        : role === 'NURSE'
          ? 'Nrs'
          : role === 'COMMUNITY_CM'
            ? 'CM'
            : role === 'CPST_TBS_STAFF'
              ? 'CPST'
              : role === 'SUD_CM'
                ? 'SUD'
                : role === 'UDS_TECH'
                  ? 'UDS'
                  : role === 'GROUP_STAFF'
                    ? 'Grp'
                    : null;

  return (
    <div
      className={clsx(
        'sticky top-0 z-10 border-b border-line px-2 py-2',
        isGroup ? 'border-t-2 border-t-brand-400 bg-brand-50/40' : 'bg-surface-subtle',
      )}
      title={`${resource.type} · ${placeBadge.full}${role ? ` · ${role.replace(/_/g, ' ')}` : ''}`}
    >
      <div className="flex items-center gap-1.5">
        <span className={iconCls}>
          <Icon name={icon} size={13} />
        </span>
        <span className="flex-1 truncate text-xs font-semibold leading-tight text-ink">
          {resource.displayName}
        </span>
      </div>
      <div className="mt-1 flex items-center gap-1 text-2xs">
        <span
          className={clsx(
            'inline-flex items-center rounded px-1.5 py-px font-medium ring-1 ring-inset',
            placeBadge.cls,
          )}
        >
          {placeBadge.label}
        </span>
        {roleShort && (
          <span className="inline-flex items-center rounded bg-slate-50 px-1.5 py-px font-medium text-slate-600 ring-1 ring-inset ring-slate-200">
            {roleShort}
          </span>
        )}
        {isGroup && (
          <span className="ml-auto font-semibold tabular-nums text-brand-700">
            {resource.capacity}
          </span>
        )}
      </div>
      {isGroup && (resource as any).roomName && (
        <div className="mt-0.5 truncate text-2xs italic text-slate-500" title={(resource as any).roomName}>
          @ {(resource as any).roomName}
        </div>
      )}
    </div>
  );
}

function RowSlot({
  minute,
  idx,
  rowMinutes,
  resources,
  cells,
  onOpen,
}: {
  minute: number;
  idx: number;
  rowMinutes: number;
  resources: Array<{ id: string; type: string; displayName: string; capacity: number }>;
  cells: Array<{
    resourceId: string;
    startMin: number;
    endMin: number;
    kind: 'GROUP' | 'INDIVIDUAL';
    serviceType: string;
    placeOfService: string;
    clients: Array<{ id: string | null; name: string; cohortId: string | null; instanceId: string; status: string }>;
  }>;
  onOpen: (
    cell: { resourceId: string; startMin: number; endMin: number; clients: any[] },
    resourceName: string,
    resourceType: string,
  ) => void;
}) {
  const isHour = minute % 60 === 0;
  return (
    <>
      <div
        className={clsx(
          'border-b border-line-subtle px-2 py-1 text-right text-2xs',
          isHour ? 'font-medium text-ink-muted' : 'text-ink-subtle',
          idx % 2 === 0 ? 'bg-surface-subtle/60' : 'bg-white',
        )}
      >
        {fmtTime(minute)}
      </div>
      {resources.map((r) => {
        // Codex #8: previously `cells.find` only rendered the FIRST cell that
        // started in this row, hiding parallel bookings on capacity>1
        // resources (e.g. a UDS station double-booked, or two services
        // sharing a 30-min row). Render ALL of them; we stack them in
        // the same cell-div, each absolute-positioned, so overlaps are
        // visible as side-by-side chips.
        const startsHere = cells.filter(
          (c) =>
            c.resourceId === r.id &&
            c.startMin >= minute &&
            c.startMin < minute + rowMinutes,
        );
        // Column body tint by resource type — at-a-glance differentiation.
        const colTint =
          r.type === 'GROUP'
            ? idx % 2 === 0
              ? 'bg-brand-50/30'
              : 'bg-brand-50/15'
            : r.type === 'COMMUNITY_PROVIDER'
              ? idx % 2 === 0
                ? 'bg-amber-50/25'
                : 'bg-amber-50/10'
              : r.type === 'ROOM'
                ? idx % 2 === 0
                  ? 'bg-emerald-50/25'
                  : 'bg-emerald-50/10'
                : idx % 2 === 0
                  ? 'bg-surface-subtle/40'
                  : 'bg-white';
        return (
          <div
            key={r.id}
            className={clsx(
              'relative h-12 border-b border-l border-line-subtle',
              colTint,
            )}
          >
            {startsHere.map((cell, i) => (
              <Cell
                key={`${cell.resourceId}|${cell.startMin}|${cell.serviceType}|${i}`}
                cell={cell}
                resource={r}
                rowMinutes={rowMinutes}
                stackIndex={i}
                stackCount={startsHere.length}
                onOpen={(c) => onOpen(c, r.displayName, r.type)}
              />
            ))}
          </div>
        );
      })}
    </>
  );
}

function Cell({
  cell,
  resource,
  rowMinutes,
  stackIndex = 0,
  stackCount = 1,
  onOpen,
}: {
  cell: {
    startMin: number;
    endMin: number;
    kind: 'GROUP' | 'INDIVIDUAL';
    serviceType: string;
    clients: Array<{ id: string | null; name: string; cohortId: string | null; instanceId: string; status: string }>;
  };
  resource: { capacity: number; type: string };
  rowMinutes: number;
  /** Index of this cell within the row's stack. Used to side-by-side
   * overlapping bookings on capacity>1 resources. */
  stackIndex?: number;
  stackCount?: number;
  onOpen: (c: any) => void;
}) {
  const rowsSpan = Math.max(1, (cell.endMin - cell.startMin) / rowMinutes);
  const height = `calc(${rowsSpan * 48}px - 4px)`;
  const isGroup = cell.kind === 'GROUP';
  const count = cell.clients.length;
  const fillRatio = isGroup ? count / Math.max(1, resource.capacity) : 0;
  const fillTone = fillRatio >= 0.95 ? 'danger' : fillRatio >= 0.85 ? 'warn' : 'normal';

  // Color the individual cell by attendance status so the calendar
  // self-documents at a glance: emerald=attended, amber=late, rose=no-show,
  // slate=cancelled, brand=scheduled.
  const indivStatus = cell.clients[0]?.status ?? 'SCHEDULED';
  const indivTone =
    indivStatus === 'ATTENDED'
      ? 'border-emerald-400 bg-emerald-50 text-emerald-900 hover:bg-emerald-100'
      : indivStatus === 'LATE'
        ? 'border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100'
        : indivStatus === 'NO_SHOW'
          ? 'border-rose-400 bg-rose-50 text-rose-900 hover:bg-rose-100'
          : indivStatus.startsWith('CANCELLED') || indivStatus === 'RESCHEDULED'
            ? 'border-slate-300 bg-slate-50 text-slate-600 hover:bg-slate-100'
            : 'border-emerald-300 bg-emerald-50 text-emerald-900 hover:bg-emerald-100';

  // When multiple cells start in the same row (capacity>1 resources or
  // overlapping bookings), split the width evenly so all stacked cells are
  // visible side-by-side.
  const widthPct = 100 / Math.max(1, stackCount);
  const leftPct = stackIndex * widthPct;
  return (
    <button
      type="button"
      onClick={() => onOpen(cell)}
      className={clsx(
        'absolute z-[1] flex flex-col items-start justify-center gap-0.5 overflow-hidden rounded-md px-2 py-1 text-left text-xs shadow-sm transition',
        isGroup
          ? fillTone === 'danger'
            ? 'border border-rose-300 bg-rose-50 text-rose-900 hover:bg-rose-100'
            : fillTone === 'warn'
              ? 'border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100'
              : 'border border-brand-300 bg-brand-50 text-brand-900 hover:bg-brand-100'
          : `border ${indivTone}`,
      )}
      style={{
        height,
        top: 2,
        left: `calc(${leftPct}% + 4px)`,
        width: `calc(${widthPct}% - 8px)`,
      }}
    >
      {isGroup ? (
        <>
          <div className="flex w-full items-center justify-between gap-1">
            <span className="truncate font-semibold">{ServiceLabels[cell.serviceType as 'PHP']}</span>
            <span className="shrink-0 rounded bg-white/70 px-1 text-2xs font-semibold tabular-nums">
              {count}/{resource.capacity}
            </span>
          </div>
          <div className="text-2xs opacity-70">
            {fmtTime(cell.startMin)}–{fmtTime(cell.endMin)} · tap for roster
          </div>
        </>
      ) : (
        <>
          <div className="flex w-full items-center gap-1.5">
            <span className="font-mono text-2xs font-semibold">{cell.clients[0]?.cohortId ?? ''}</span>
            <span className="truncate font-medium">{cell.clients[0]?.name ?? ''}</span>
            {indivStatus !== 'SCHEDULED' && (
              <span className="ml-auto shrink-0 rounded bg-white/80 px-1 text-2xs font-semibold tabular-nums">
                {indivStatus}
              </span>
            )}
          </div>
          <div className="truncate text-2xs opacity-70">
            {ServiceLabels[cell.serviceType as 'THERAPY']} · {fmtTime(cell.startMin)} · tap for actions
          </div>
        </>
      )}
    </button>
  );
}

function StatusPill({ status }: { status: string }) {
  const cls =
    status === 'ATTENDED' || status === 'LATE'
      ? 'pill-success'
      : status === 'NO_SHOW'
        ? 'pill-danger'
        : status === 'SCHEDULED'
          ? 'pill-info'
          : 'pill-muted';
  return <span className={cls}>{status}</span>;
}

interface RosterCell {
  resourceId: string;
  resourceName: string;
  resourceType: string;
  startMin: number;
  endMin: number;
  clients: Array<{
    id: string | null;
    name: string;
    cohortId: string | null;
    status: string;
    instanceId?: string;
  }>;
}

/**
 * Cell action modal — opened by tapping any cell on the calendar.
 *   GROUP cells → Roster + Candidates-to-add panes (existing behavior).
 *   INDIVIDUAL cells → Client header + action buttons (check-in / late /
 *                      no-show / cancel / reschedule).
 *
 * The terminal-status guard on the backend (instances.service.ts
 * `assertMutable`) blocks invalid transitions, so the UI can offer the full
 * action set without client-side gating — failures surface as toasts with
 * the exact server message.
 */
function GroupRosterModal({ cell, onClose }: { cell: RosterCell; onClose: () => void }) {
  const qc = useQueryClient();
  const isGroup = cell.resourceType === 'GROUP';
  // Show the action panel for any non-group cell that has at least one
  // client on it. We render the buttons even when `instanceId` is missing
  // (rare edge — see fallback inside IndividualActionPanel) so the user
  // always has a way to act, instead of getting a status-only view.
  const isIndividual = !isGroup && !!cell.clients[0];

  const { data: candidatesData, isLoading: candidatesLoading } = useQuery({
    queryKey: ['group-candidates', cell.resourceId],
    queryFn: () =>
      api.get(`/resources/${cell.resourceId}/candidates`).then((r) => r.data),
    enabled: isGroup,
  });

  const invalidateSchedule = () => {
    qc.invalidateQueries({ queryKey: ['resource-day'] });
    qc.invalidateQueries({ queryKey: ['daily-sheet'] });
    qc.invalidateQueries({ queryKey: ['group-candidates', cell.resourceId] });
  };
  const onMutationError = (err: any) => {
    const msg =
      err?.response?.data?.message ??
      err?.response?.data?.violations?.[0]?.message ??
      'Action blocked';
    toast.error(msg);
  };

  const enroll = useMutation({
    mutationFn: ({ clientId, enrolledDays }: { clientId: string; enrolledDays: string[] }) =>
      api
        .post(`/resources/${cell.resourceId}/enroll`, { clientId, enrolledDays })
        .then((r) => r.data),
    onSuccess: () => {
      toast.success('Client enrolled');
      invalidateSchedule();
    },
    onError: onMutationError,
  });

  // ── Individual-cell mutations ────────────────────────────────────────────
  const checkIn = useMutation({
    mutationFn: ({ id, lateMinutes }: { id: string; lateMinutes?: number }) =>
      api.post(`/instances/${id}/check-in`, { lateMinutes }).then((r) => r.data),
    onSuccess: () => {
      toast.success('Checked in');
      invalidateSchedule();
      onClose();
    },
    onError: onMutationError,
  });
  const noShow = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post(`/instances/${id}/no-show`, { reason }).then((r) => r.data),
    onSuccess: () => {
      toast.success('Marked no-show');
      invalidateSchedule();
      onClose();
    },
    onError: onMutationError,
  });
  const cancel = useMutation({
    mutationFn: ({
      id,
      by,
      reason,
    }: {
      id: string;
      by: 'CLIENT' | 'PROVIDER' | 'CLINIC';
      reason: string;
    }) => api.post(`/instances/${id}/cancel`, { by, reason }).then((r) => r.data),
    onSuccess: () => {
      toast.success('Cancelled');
      invalidateSchedule();
      onClose();
    },
    onError: onMutationError,
  });
  const reschedule = useMutation({
    mutationFn: ({
      id,
      newDate,
      newStartMin,
      newEndMin,
      reason,
    }: {
      id: string;
      newDate: string;
      newStartMin: number;
      newEndMin: number;
      reason?: string;
    }) =>
      api
        .post(`/instances/${id}/reschedule`, { newDate, newStartMin, newEndMin, reason })
        .then((r) => r.data),
    onSuccess: () => {
      toast.success('Rescheduled');
      invalidateSchedule();
      onClose();
    },
    onError: onMutationError,
  });

  return (
    <div
      className="fixed inset-0 z-40 grid place-items-center bg-ink/40 p-4"
      onClick={onClose}
    >
      <div className="card w-full max-w-2xl p-5" onClick={(e) => e.stopPropagation()}>
        <header className="mb-3 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-ink">{cell.resourceName}</h3>
            <p className="text-xs text-ink-subtle">
              {fmtTime(cell.startMin)} – {fmtTime(cell.endMin)} · {cell.clients.length} clients
            </p>
          </div>
          <button
            className="grid h-8 w-8 place-items-center rounded-md text-ink-subtle hover:bg-surface-muted"
            onClick={onClose}
          >
            <Icon name="x" size={16} />
          </button>
        </header>

        {/* Individual cell → action panel. Branch first so we don't render the
            group roster/candidates UI for a single-client cell. */}
        {isIndividual && cell.clients[0] && (() => {
          const c0 = cell.clients[0]!;
          const guard = () => {
            if (!c0.instanceId) {
              toast.error(
                'No AppointmentInstance row exists for this slot yet. Refresh the page or check the schedule view.',
              );
              return false;
            }
            return true;
          };
          return (
          <IndividualActionPanel
            client={c0}
            startMin={cell.startMin}
            endMin={cell.endMin}
            onCheckIn={() => guard() && checkIn.mutate({ id: c0.instanceId! })}
            onLate={() => {
              if (!guard()) return;
              const raw = prompt('Late by how many minutes?');
              const m = raw ? Number(raw) : NaN;
              if (!Number.isFinite(m) || m <= 0) return;
              checkIn.mutate({ id: c0.instanceId!, lateMinutes: m });
            }}
            onNoShow={() => {
              if (!guard()) return;
              const reason = prompt('No-show reason?') || 'Unspecified';
              noShow.mutate({ id: c0.instanceId!, reason });
            }}
            onCancel={(by) => {
              if (!guard()) return;
              const reason = prompt('Cancellation reason?') || 'Unspecified';
              cancel.mutate({ id: c0.instanceId!, by, reason });
            }}
            onReschedule={() => {
              if (!guard()) return;
              const newDate = prompt('New date (YYYY-MM-DD)?');
              if (!newDate) return;
              const newTime = prompt('New start time (HH:MM, 24-hour)?');
              if (!newTime) return;
              const m = /^(\d{1,2}):(\d{2})$/.exec(newTime);
              if (!m) {
                toast.error('Time must be HH:MM');
                return;
              }
              const startMin = Number(m[1]) * 60 + Number(m[2]);
              const duration = cell.endMin - cell.startMin;
              const endMin = startMin + duration;
              const reason = prompt('Reason for reschedule?') || undefined;
              reschedule.mutate({
                id: c0.instanceId!,
                newDate,
                newStartMin: startMin,
                newEndMin: endMin,
                reason,
              });
            }}
            pending={
              checkIn.isPending ||
              noShow.isPending ||
              cancel.isPending ||
              reschedule.isPending
            }
          />
          );
        })()}

        {!isIndividual && (
        <div className="grid gap-4 md:grid-cols-2">
          {/* Current roster */}
          <section>
            <h4 className="mb-2 text-2xs font-semibold uppercase tracking-wide text-ink-subtle">
              Roster ({cell.clients.length})
            </h4>
            <ul className="max-h-[55vh] divide-y divide-line-subtle overflow-y-auto rounded border border-line">
              {cell.clients.length === 0 ? (
                <li className="px-2 py-3 text-sm text-ink-subtle">No clients enrolled yet</li>
              ) : (
                cell.clients.map((c, i) => (
                  <li
                    key={i}
                    className="flex items-center justify-between gap-2 px-2 py-2 text-sm"
                  >
                    <span className="font-mono text-brand-700">{c.cohortId ?? '—'}</span>
                    <span className="flex-1 truncate">{c.name}</span>
                    <StatusPill status={c.status} />
                  </li>
                ))
              )}
            </ul>
          </section>

          {/* Candidates to add */}
          {isGroup && (
            <section>
              <h4 className="mb-2 text-2xs font-semibold uppercase tracking-wide text-ink-subtle">
                Candidates to add{' '}
                {candidatesData ? `(${candidatesData.candidates?.length ?? 0})` : ''}
              </h4>
              {candidatesLoading ? (
                <p className="text-sm text-ink-subtle">Loading…</p>
              ) : (candidatesData?.candidates?.length ?? 0) === 0 ? (
                <p className="rounded border border-line bg-surface-subtle px-2 py-3 text-xs text-ink-subtle">
                  {candidatesData?.reason ??
                    'No qualifying clients available to add (all eligible cohort members are already enrolled, conflicted, or the group is full).'}
                </p>
              ) : (
                <ul className="max-h-[55vh] divide-y divide-line-subtle overflow-y-auto rounded border border-line">
                  {candidatesData!.candidates.map((c: any) => (
                    <li key={c.clientId} className="px-2 py-2 text-sm">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-brand-700">{c.cohortId}</span>
                            <span className="truncate font-medium">{c.name}</span>
                          </div>
                          <div className="mt-0.5 text-2xs text-ink-subtle">
                            Safe on {c.suggestedDays.join(', ')}
                            {c.conflicts.length > 0 && (
                              <span className="ml-1 text-amber-700">
                                · {c.conflicts.length} day(s) blocked
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          className="btn-primary h-7 px-2 text-2xs"
                          disabled={enroll.isPending}
                          onClick={() =>
                            enroll.mutate({
                              clientId: c.clientId,
                              enrolledDays: c.suggestedDays,
                            })
                          }
                        >
                          {enroll.isPending ? 'Adding…' : 'Add'}
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-2 text-2xs text-ink-subtle">
                Backend re-runs the full conflict check on every Add — listed candidates are pre-filtered, never trusted.
              </p>
            </section>
          )}
        </div>
        )}
      </div>
    </div>
  );
}

function IndividualActionPanel({
  client,
  startMin,
  endMin,
  onCheckIn,
  onLate,
  onNoShow,
  onCancel,
  onReschedule,
  pending,
}: {
  client: { name: string; cohortId: string | null; status: string };
  startMin: number;
  endMin: number;
  onCheckIn: () => void;
  onLate: () => void;
  onNoShow: () => void;
  onCancel: (by: 'CLIENT' | 'PROVIDER' | 'CLINIC') => void;
  onReschedule: () => void;
  pending: boolean;
}) {
  const isTerminal =
    client.status === 'NO_SHOW' ||
    client.status === 'EXCUSED' ||
    client.status === 'RESCHEDULED' ||
    client.status.startsWith('CANCELLED');
  return (
    <section className="space-y-3">
      <div className="rounded border border-line-subtle bg-surface-subtle/40 px-3 py-2 text-sm">
        <div className="flex items-center gap-2">
          <span className="font-mono text-brand-700">{client.cohortId ?? '—'}</span>
          <span className="font-medium">{client.name}</span>
          <span className="ml-auto">
            <StatusPill status={client.status} />
          </span>
        </div>
        <div className="mt-1 text-xs text-ink-subtle">
          {fmtTime(startMin)} – {fmtTime(endMin)}
        </div>
      </div>

      {isTerminal && (
        <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          This instance is in a terminal status ({client.status}). Most actions are blocked —
          use Reschedule to create a follow-up, or open the client detail page to admin-reset.
        </p>
      )}

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <button
          className="btn-primary btn-touch"
          disabled={pending || isTerminal}
          onClick={onCheckIn}
        >
          ✓ Attended
        </button>
        <button
          className="btn-secondary btn-touch"
          disabled={pending || isTerminal}
          onClick={onLate}
        >
          ⏱ Late
        </button>
        <button
          className="btn-danger btn-touch"
          disabled={pending || isTerminal}
          onClick={onNoShow}
        >
          ✗ No-show
        </button>
        <button
          className="btn-secondary btn-touch"
          disabled={pending || isTerminal}
          onClick={() => onCancel('CLIENT')}
        >
          Cancel (client)
        </button>
        <button
          className="btn-secondary btn-touch"
          disabled={pending || isTerminal}
          onClick={() => onCancel('PROVIDER')}
        >
          Cancel (provider)
        </button>
        <button
          className="btn-secondary btn-touch"
          disabled={pending}
          onClick={onReschedule}
        >
          ⤴ Reschedule
        </button>
      </div>

      <p className="text-2xs text-ink-subtle">
        Reschedule creates a new instance in the requested slot and marks this one RESCHEDULED.
        The backend runs the same conflict check used for manual create — invalid slots will be
        rejected with the specific violation.
      </p>
    </section>
  );
}
